/*globals Sentry:false */

import { ConfirmDialog } from '@cob/ui-genesis/helpers/cob.dialog.js';
import DuplicateFieldsPresenter from './recordm.presenter.instance.duplicatefield.js';
import FieldPresenter from './recordm.presenter.instance.field.js';
import InstanceM from '../model/recordm.model.entity.instance.js';
import InstanceV from '../view/recordm.view.instance.js';
import PubSub from '@cob/ui-genesis/helpers/cob.pubsub.js';

// args:
// * `core`: cob.app
// * `$container`: The html container
// * `instanceM`: the instanceM
// * `clipboardP`: the clipboard presenter
// * `callbacks`: this Presenter accepts 2 callbacks:
//       * `cancel`: called when the user cancels the create/edit/view
//       * `afterSave`: called after the instance is saved
// * `opts`: accepts:
//       * `exitLabel`: a localize key to be used on the exit (back) button
const InstanceP = function(core, $container, instanceM, clipboardP, callbacks, opts) {

    const internal = new PubSub();

    var _fieldPs = [],
        _dupPs = [],
        _opts = { ...opts };

    var _canEdit = instanceM.canUpdate() || instanceM.data.id < 0;
    const _canFix = instanceM.canFix();

    var _loadingKey = core.getLoadingKey();

    var _setViewOpts = function() {
        _opts.edit = false;
        _opts.mandatory = false;
        _opts.validate = false;
    };

    var _setEditOpts = function(isNewInstance) {
        _opts.edit = true;
        _opts.create = isNewInstance || false;
        _opts.mandatory = true;
        _opts.validate = true;
    };

    const saveBehaviorsM =  core.getSaveBehaviorsM(instanceM.getDefinitionName());

    const _saveInstance = function(saveBehaviorName) {
        const afterSaveCb = saveBehaviorsM.getActionFor(saveBehaviorName, callbacks);
        _saveChanges(afterSaveCb);
    };

    const view = new InstanceV(core, $container, {
            canEdit: _canEdit,
            canFix: _canFix,
            exitLabel: _opts.exitLabel,
            saveBehaviors: saveBehaviorsM.getBehaviors()
        },
        {
            collapseAll: () => internal.publish('collapse-all'),
            expandAll: () => internal.publish('expand-all'),
            exit: callbacks.cancel,
            save: _saveInstance
        }
    );
    view.init();

    const _saveOrConfirm = function(updatedInstanceM, afterSaveCb){
       const isUpToDate = updatedInstanceM.isUpToDate();
       if(!isUpToDate){
          ConfirmDialog(core, {
             message: core.translateString('recordm.instances', 'msgs.error-outdated-pre-save'),
             confirmBtnLabel: core.translateString('recordm.instances', 'action.save')
          }, {
             requestConfirmed: () => _executeSave(afterSaveCb, true)
          });
       } else {
          _executeSave(afterSaveCb)
       }
    }

    const _saveChanges = function(afterSaveCb) {
       // we need localIds for validating on creation
       const updatedInstanceM = new InstanceM(core, _getModel(true));

       if(core.inFixMode()) {
          _saveOrConfirm(updatedInstanceM, afterSaveCb);

          return;
       }

       const isValid = _validate();

       core.applyValidations(instanceM.getDefinitionName(), updatedInstanceM, instanceM, {
          pass: function(){
             if (isValid) {
                _saveOrConfirm(updatedInstanceM, afterSaveCb);

             } else {
                view.showErrorMsg();
             }
          },
          fail: function(errors, showDefaultError){
             if(showDefaultError) view.showErrorMsg();

             if(errors){
                //todo jone unificar isto e o do fieldError
                _fieldPs.forEach(function(fieldP) {
                   fieldP.presentErrors(errors);
                });

                _dupPs.forEach(function(dupP) {
                   dupP.presentErrors(errors);
                });
             }
             view.enableSaveBtn();
          }
       })
    };

    const _executeSave = function(afterSaveCb, acceptOutdated){
       view.disableSaveBtn();

       core.showLoading(_loadingKey, "Saving instance");

       view.clearErrors();

       instanceM.sync(_getModel(true), {
          params: _opts.params,
          acceptOutdated: acceptOutdated,
          fix: _canFix && core.inFixMode() ,
          ignoreErrors: _opts.create,
          success: function() {
             core.hideLoading(_loadingKey, "Saving instance finished successfully");

             if (typeof afterSaveCb === 'function') afterSaveCb(instanceM.data);
          },
          error: function(req) {
             if(req.status === 503 && req.responseText.startsWith('OUTDATED_EXT_REF')){
                ConfirmDialog(core, {
                   message: core.translateString('recordm.instances', 'msgs.error-outdated-on-server'),
                   confirmBtnLabel: core.translateString('recordm.instances', 'action.save')
                }, {
                   requestConfirmed: () => _executeSave(afterSaveCb, true)
                });
             }
             core.hideLoading(_loadingKey, "Error Saving instance");
             view.enableSaveBtn();
          },
          fieldError: function(errors) {
             if (errors) {

                view.showErrorMsg();
                const processedErrors = errors.map((error) => {
                   const processed = {
                      //each error comes in the for "fieldID:fieldName:errortype"
                       fieldId: parseInt(error.field.split(":")[0], 10),
                       type: error.errorType,
                       l10nSource: error.l10nSource,
                       l10nArgs: error.l10nArgs
                   };

                   // Remove do inicio a parte que não interessa e que já vem no error.field
                   if(!error.errorType && error.error) processed.localizedMessage = error.error.replaceAll(error.field + ":", "");

                   return processed
                })

                _fieldPs.forEach(function(fieldP) {
                   fieldP.presentErrors(processedErrors);
                });

                _dupPs.forEach(function(dupP) {
                   dupP.presentErrors(processedErrors);
                });
             }

             core.hideLoading(_loadingKey, "Error Saving instance due to errors in fields");
             view.enableSaveBtn();
          }
       });
    }

    var _validate = function() {
        var allOk = true;

        _fieldPs.forEach(function(fieldP) {
            allOk = fieldP.validate() && allOk;
        });

        _dupPs.forEach(function(dupP) {
            allOk = dupP.validate() && allOk;
        });

        return allOk;
    };

    var _getModel = function(withLocalIds) {

        var instanceData = instanceM.data,
            instanceModel = {};

        instanceModel.jsonDefinition = instanceData.jsonDefinition;
        instanceModel.id = instanceData.id;
        instanceModel.attachmentPath = instanceData.attachmentPath;
        instanceModel.version = instanceData.version;
        instanceModel.fields = [];

        instanceModel.instanceLabel = instanceData.instanceLabel;
        instanceModel._links = instanceData._links;

        _fieldPs.forEach(function(el) {
            instanceModel.fields.push(el.getModel(withLocalIds));
        });

        if (_dupPs) {
            _dupPs.forEach(function(el) {
                el.fillModel(instanceModel, withLocalIds);
            });
        }

        if (instanceData.id < 0 && !withLocalIds) {
            instanceModel.id = undefined;
        }

        return instanceModel;
    };

    var _init = function(ignoreDefaults, isNewInstance) {
        var instanceData = instanceM.data;

        if(isNewInstance){
            instanceM.setNew();
        }

        _fieldPs = [];
        _dupPs = [];

        if (_canEdit && !instanceM.isDefinitionDisabled()) {
            _setEditOpts(isNewInstance);

        } else {
            _setViewOpts();
        }

        view.showInstance(instanceData);

        if (instanceData.fields) {
            instanceData.fields.map(function(instanceField) {
                if (instanceField.fieldDefinition.duplicable) {

                    if(!(instanceField.fieldDefinition.id in _dupPs)){
                        _dupPs[instanceField.fieldDefinition.id] = new DuplicateFieldsPresenter(
                            core, internal, view, instanceData, instanceField, {
                                isField: false,
                               // used by $auto fields to find target fields outside their ancestor tree
                               findSibling: function(name){
                                  return _findFieldPsIn(_fieldPs.concat(_dupPs), name );
                               }
                            } , _opts, ignoreDefaults,
                            clipboardP, {}
                        );
                    } else {
                        _dupPs[instanceField.fieldDefinition.id].addDuplicate(null, instanceField);
                    }
                } else {

                    var newFieldP = new FieldPresenter(
                        core, internal, instanceData, instanceField, {
                            isField: false,
                           // used by $auto fields to find target fields outside their ancestor tree
                           findSibling: function(name){
                              return _findFieldPsIn(_fieldPs.concat(_dupPs), name );
                           }
                        }, _opts, ignoreDefaults, clipboardP, {}
                    );
                    _fieldPs.push(newFieldP);
                    view.addField(newFieldP);
                }
            });
        }

        //trigger textaereas autoresize
        view.autoResizeTextAreas();

        // We publish 2 instance-ready events so that we can securely unsubscribe all subscribers of the internal one in
        // the end of this presenter and we can let the main version for public subscription usage (like we may
        // do in customizations.js).
        // This approach is kind of an hack because we could't get rid of all the subscriptions made in this presenter
        // and there were subscribers being left in the bus (and we could not just unsubscribe everything beacause of the
        // customizations.js being wrongly unsubscribed).
        core.publish("instance-ready");
        internal.publish("instance-ready-internal");

        /* eslint-disable-next-line no-invalid-this  */
        core.applyCustomizations(instanceM.getDefinitionName(), instanceM, {
            findFieldPs: function(predicate){
                return _findFieldPsIn(_fieldPs.concat(_dupPs), predicate)
            },

            findFieldPsUnder: function(fieldP, predicate){
                const allChildren = fieldP.getChildPs().concat(fieldP.getChildDuPs());
                return _findFieldPsIn(allChildren, predicate);
            },

            onFieldChange: function(field, listener){
                if(typeof field === 'string'){
                    if((/%[0-9a-zA-Z]{2}/).test(field)){
                       window.console.warn('onFieldChange called with already encoded field name, please remove it.')
                       if(typeof Sentry !== 'undefined') Sentry.captureMessage(
                          'onFieldChange called with already encoded field name, please remove it.'
                       );

                    } else {
                       field = encodeURIComponent(field);
                    }
                    core.instanceChangeListener(instanceM.getDefinitionName(), 'field-value-change-byName-' + field, listener);

                } else if(typeof field.getChildPs !== 'undefined') {
                    // it's a field presenter
                    core.instanceChangeListener(instanceM.getDefinitionName(), 'field-value-change-' + field.getId(), listener);

                } else if(typeof field.fieldDefinition !== 'undefined') {
                    // it's an instance field
                    core.instanceChangeListener(instanceM.getDefinitionName(), 'field-value-change-' + field.id, listener);

                } else {
                    window.console.warn("don't know what to do with this", field)
                }
            },

            getData: function() {
                return _getModel(true)
            },

            //Deprecated: should use RecordmCustomizingHelper.customizeSaveBehaviors() instead
            saveInstance: function(afterSaveCb) {
                if(typeof afterSaveCb === 'function'){
                    _saveChanges(afterSaveCb);
                }
            },

            isGroupEdit: function() {
                return false;
            },

            /**
             * @param action
             * {
             *   "label": "Label" // The label for its button
             *   "callback": function($btn){} // function to call when button is clicked (may receive the jQuery button as an argument)
             *   "cssClasses": "class-1 class-2" // (optional) CSS classes to add to the button
             * }
             **/
            addAction: function (action={}){
                if(action.label && action.callback && typeof action.callback === "function"){
                    view.addAction(action);
                } else {
                    console.warn("There are some missing properties for the given action (label or callback)", action);
                }
            },

            showLoading: function(msg){
                core.showLoading(_loadingKey, msg);
            },

            hideLoading: function(msg){
                core.hideLoading(_loadingKey, msg);
            }
        });

       core.startListeners(internal, instanceM.getDefinitionName());
    };

    var _findFieldPs = function(fieldAndDupPs, predicate){
       var _matched = [];

       fieldAndDupPs.forEach((p) => {

          if(typeof p.addDuplicate === 'undefined'){
             // regular FieldPresenter
             if(predicate(p)) _matched.push(p);

             if(p.getChildPs()) _matched = _matched.concat(_findFieldPs(p.getChildPs(), predicate))
             if(p.getChildDuPs()) _matched = _matched.concat(_findFieldPs(p.getChildDuPs(), predicate))

          } else {
             // a DupFieldPresenter
             if(p.getFieldPs()) _matched = _matched.concat(_findFieldPs(p.getFieldPs(), predicate))
          }
       })

       return _matched;
    }

   var _findFieldPsIn = function(children, predicate){
      if(typeof predicate === 'string'){
         return _findFieldPs(children, function(fp){
            return  fp.isVisible() && fp.field.fieldDefinition.name === predicate
         })
      }
      return _findFieldPs(children, predicate);
   }


    var _destroy = function() {
        core.stopListeners(internal, instanceM.getDefinitionName());

        _fieldPs.forEach(function(_fieldP) {
            _fieldP.destroy();
        });
        _dupPs.forEach(function(_dupP) {
            _dupP.destroy();
        });

        view.destroy();
    };

    return{
        init: _init,
        destroy: _destroy,

        findFieldPs: function(predicate){
            return _findFieldPsIn(_fieldPs.concat(_dupPs), predicate)
        }
    }
};

export default InstanceP;
